import { useState, useCallback, useEffect, use } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useAxios from '@/hooks/use-axios';
import { base64ToFile } from '@/lib/utils';
import { Slider } from '@/components/ui/slider';

import { Download, Image as ImageIcon, Square, Link2 } from 'lucide-react';
const QRForm = ({ OnGenerated, hideInputContent = false, hideInputName = true, config, ...props }) => {
  const safeEmit = (callback, payload) => {
    if (typeof callback === 'function') {
      callback(payload);
    } else {
      console.warn('Parent callback tidak tersedia');
    }
  };
  
  const qrConfig = config?.qr_config || {}
  const CORNER_STYLES = [
    { value: 'square', label: 'Square', icon: Square },
    { value: 'rounded', label: 'Rounded', icon: Square },
    // { value: "circle", label: "Circle", icon: Circle },
    // { value: "gapped", label: "Gapped", icon: Grid3X3 },
  ];

  const STYLE_MODES = [
    { value: 'basic', label: 'Basic', className: 'garuda-shape-basic' },
    { value: 'artistic', label: 'Artistic', className: 'garuda-shape-artistic' },
    { value: 'gapped', label: 'Square', className: 'garuda-shape-square' },
    { value: 'rounded', label: 'Rounded', className: 'garuda-shape-rounded' },
    { value: 'half-rounded', label: 'Half Rounded', className: 'garuda-shape-half-rounded'},
    { value: 'circle', label: 'Circle', className: 'garuda-shape-circle' },
    { value: 'horizontal', label: 'Horizontal', className: 'garuda-shape-horizontal' },
    { value: 'vertical', label: 'Vertical', className: 'garuda-shape-vertical' },
    { value: 'crystal-block', label: 'Crystal Block', className: 'garuda-shape-crystal-block' },
    { value: 'diamond', label: 'Diamond', className: 'garuda-shape-diamond' },
    { value: 'parallelogram', label: 'Parallelog-ram', className: 'garuda-shape-parallelogram' },
    { value: 'sparkle', label: 'Sparkle', className: 'garuda-shape-sparkle' },
    { value: 'circuit', label: 'Circuit', className: 'garuda-shape-circuit' },
    { value: 'cross', label: 'Cross', className: 'garuda-shape-cross' },
    { value: 'cyberpunk', label: 'Cyberpunk', className: 'garuda-shape-cyberpunk' },
    { value: 'full-diamond', label: 'Full Diamond', className: 'garuda-shape-full-diamond' },
    { value: 'full-sparkle', label: 'Full Sparkle', className: 'garuda-shape-full-sparkle' },
    { value: 'flower', label: 'Flower', className: 'garuda-shape-flower' },
  ];

  const GRADIENT_DIRECTIONS = [
    { value: 'diagonal', label: 'Diagonal' },
    { value: 'vertical', label: 'Vertical' },
    { value: 'horizontal', label: 'Horizontal' },
    { value: 'radial', label: 'Radial' },
  ];


  const EYE_FRAME_SHAPES = [
    { value: 'square', label: 'Square', className: 'garuda-border-square'  },
    { value: 'rounded', label: 'Rounded', className: 'garuda-border-rounded'  },
    { value: 'circle', label: 'Circle', className: 'garuda-border-circle'},
    { value: 'leaf', label: 'Leaf', className: 'garuda-border-leaf' },
    { value: 'drop', label: 'Drop', className: 'garuda-border-drop' },
    { value: 'dotted', label: 'Dotted', className: 'garuda-border-dotted'},
    { value: 'rounded-corner', label: 'Rounded Corner', className: 'garuda-border-rounded-corner' },
    { value: 'crystal-block', label: 'Crystal Block', className: 'garuda-border-crystal-block' },
  ];

  const EYE_BALL_SHAPES = [
    { value: 'square', label: 'Square', className: 'garuda-center-square' },
    { value: 'circle', label: 'Circle', className: 'garuda-center-circle' },
    { value: 'rounded', label: 'Rounded', className: 'garuda-center-rounded'  },
    { value: 'rounded-corner', label: 'Rounded Corner', className: 'garuda-center-rounded-corner' },
    { value: 'leaf', label: 'Leaf', className: 'garuda-center-leaf' },
    { value: 'dotted', label: 'Dotted', className: 'garuda-center-dotted' },
    { value: 'crystal-block', label: 'Crystal Block', className: 'garuda-center-crystal-block' },
    { value: 'horizontal', label: 'Horizontal', className: 'garuda-center-horizontal' },
    { value: 'vertical', label: 'Vertical', className: 'garuda-center-vertical' },
    { value: 'merged-dots', label: 'Merged Dots', className: 'garuda-center-merged-dots' },
    { value: 'diamond', label: 'Diamond', className: 'garuda-center-diamond' },
    { value: 'cross', label: 'Cross', className: 'garuda-center-cross' },
  ];


  const api = useAxios();

  const [pdfFile, setPdfFile] = useState(null);
  const [pdfInfo, setPdfInfo] = useState(null);
  const [qrContent, setQrContent] = useState(hideInputContent ? 'https://www.example.com' : qrConfig?.content || '');
  const [qrPreview, setQrPreview] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);


  const [qrPath, setQrPath] = useState(config?.qr_path || '');
  const [name, setName] = useState(config?.name || '');
  // QR Config
  const [fgColor, setFgColor] = useState(qrConfig?.fg_color || '#000000');
  const [bgColor, setBgColor] = useState(qrConfig?.bg_color || '#FFFFFF');
  const [qrSize, setQrSize] = useState(300);
  const [cornerStyle, setCornerStyle] = useState(qrConfig?.corner || 'square');

  const [logoFile, setLogoFile] = useState(null);
  const [logoBase64, setLogoBase64] = useState(qrConfig?.logo_base64 || null);
  const [enableTransparentBG, setEnableTranparentBG] = useState(qrConfig?.transparent_background || false);

  // Artistic style options
  const [styleMode, setStyleMode] = useState(qrConfig?.style_mode || 'basic');
  const [accentColor, setAccentColor] = useState(qrConfig?.accent_color || '#3B5998');
  const [cornerColor, setCornerColor] = useState(qrConfig?.corner_color || '#1E3A8A');
  const [enableCurves, setEnableCurves] = useState(qrConfig?.enable_curves || false);

  // Gradient options (available for all modes)
  const [enableGradient, setEnableGradient] = useState(qrConfig?.enable_gradient || false);
  const [gradientStart, setGradientStart] = useState(qrConfig?.gradient_start || '#1E3A8A');
  const [gradientEnd, setGradientEnd] = useState(qrConfig?.gradient_end || '#92400E');
  const [gradientDirection, setGradientDirection] = useState(
    qrConfig?.gradient_direction || 'diagonal',
  );

  // Eye frame shape and eye ball shape
  const [eyeFrameShape, setEyeFrameShape] = useState(qrConfig?.eye_frame_shape || 'square');
  const [eyeBallShape, setEyeBallShape] = useState(qrConfig?.eye_ball_shape || 'square');
  const [eyeColor, setEyeColor] = useState(qrConfig?.eye_color || '#000000'); // Custom eye/corner color for gradient mode
  const [gradientRadius, setGradientRadius] = useState(qrConfig?.gradient_percentage || 0); // Custom border radius if shape rounded

  // Background image
  const [bgImageFile, setBgImageFile] = useState();
  const [bgImageBase64, setBgImageBase64] = useState(qrConfig?.bg_image_base64 || null);
  const [enableTracking, setEnableTracking] = useState(true);
  const [isLoadingGenerate, setIsLoadingGenerate] = useState(false);

  const [mainConfig, setMainConfig] = useState(qrConfig || null);

  useEffect(()=>{
    const imageLogo = qrConfig?.logo_base64 ? async () => await base64ToFile(qrConfig?.logo_base64) : null;
    setLogoFile(imageLogo);
  }, [])
  useEffect(()=>{
  }, [props])

  // Generate QR Preview
  const generateQRPreview = useCallback(async () => {
    if (!qrContent) return;
    safeEmit(OnGenerated, {name: name, config: {}});
    setMainConfig(null);
    try {
      setIsLoadingGenerate(true);
      const objConfig = {
        content: qrContent,
        fg_color: fgColor,
        bg_color: bgColor,
        size: qrSize,
        corner_style: cornerStyle,
        logo_base64: logoBase64,
        style_mode: styleMode,
        accent_color: accentColor,
        corner_color: cornerColor,
        enable_curves: enableCurves,
        bg_image_base64: bgImageBase64,
        enable_gradient: enableGradient,
        gradient_start: gradientStart,
        gradient_end: gradientEnd,
        gradient_direction: gradientDirection,
        eye_frame_shape: eyeFrameShape,
        eye_ball_shape: eyeBallShape,
        eye_color: eyeColor, // Only send eye_color when gradient is enabled
        enable_tracking: enableTracking,
        gradient_percentage: gradientRadius,
        transparent_background: enableTransparentBG,
        qr_path: qrPath
      };
      const response = await api.post(`/demo/generate-qr-preview`, objConfig);
      setQrPreview(response.data.qr_base64);
      safeEmit(OnGenerated, {name: name, config: objConfig});
      setMainConfig(objConfig);
    } catch (error) {
      console.error('Error generating QR preview:', error);
    } finally {
      setTimeout(() => setIsLoadingGenerate(false), 400);
    }
  }, [
    qrContent,
    fgColor,
    bgColor,
    qrSize,
    cornerStyle,
    logoBase64,
    styleMode,
    accentColor,
    cornerColor,
    enableCurves,
    bgImageBase64,
    enableGradient,
    gradientStart,
    gradientEnd,
    gradientDirection,
    eyeFrameShape,
    eyeBallShape,
    eyeColor,
    gradientRadius,
    enableTransparentBG
  ]);

  // Handle background image upload
  const handleBgImageUpload = (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1];
      setBgImageBase64(base64);
      setBgImageFile(file);
    };
    reader.readAsDataURL(file);
  };

  // Debounced QR preview generation
  useEffect(() => {
    const timer = setTimeout(() => {
      generateQRPreview();
    }, 300);
    return () => clearTimeout(timer);
  }, [generateQRPreview]);

  useEffect(() => {
    const timer = setTimeout(() => {
      safeEmit(OnGenerated, {name: name, config: mainConfig});
    }, 300);
    return () => clearTimeout(timer);
  }, [name]);

  // Handle logo upload
  const handleLogoUpload = (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1];
      setLogoBase64(base64);
      setLogoFile(file);
    };
    reader.readAsDataURL(file);
  };

  // Download PDF with QR
  const handleDownload = async () => {
    if (!qrContent) {
      toast.error('Please upload a PDF and enter QR content');
      return;
    }

    if (!qrContent) {
      toast.error('Please upload a PDF and enter QR content');
      return;
    }
    const url = qrPreview;
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `preview_qr.png`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return;
  };

  return (
    <section id="demo-section" className="">
      <div className="container px-4 xl:px-0 mx-auto max-w-[1140px]">
        <div className="flex flex-col md:flex-row gap-6 w-full rounded-lg card">
          {/* Left Panel - Controls */}
          <div className="space-y-6 w-full lg:min-w-[696px]">
            { !hideInputName && <div className="">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-orange-500 border-primary-500" />
                Project Name
              </h3>
              <Label className="text-sm text-[#A1A1AA]">Project Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter project name"
                className="bg-zinc-950 border-zinc-800"
                data-testid="qr-name-input"
              />
            </div>}
            {/* QR Content */}
            { !hideInputContent && <div className="">
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-orange-500 border-primary-500" />
                Complete the content
              </h3>
              <Label className="text-sm text-[#A1A1AA]">Link</Label>
              <Input
                value={qrContent}
                onChange={(e) => setQrContent(e.target.value)}
                placeholder="https://"
                className="bg-zinc-950 border-zinc-800"
                data-testid="qr-content-input"
              />
            </div>}

            {/* QR Settings */}
            <div className="">
              <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <i className="garuda-qrcode text-primary-500" />
                Design QR Code
              </h3>

              <Tabs defaultValue="style" className="w-full">
                <TabsList className="w-full bg-zinc-950 mb-4">
                  <TabsTrigger value="style" className="text-xs">
                    Shape & Color
                  </TabsTrigger>
                  <TabsTrigger value="logo" className="text-xs">
                    Logo
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="style" className="space-y-4">
                  {/* Style Mode Toggle */}
                  <div>
                    <Label className="text-xs text-[#A1A1AA]">Shape Style</Label>
                    <div className="flex flex-wrap gap-[10px] mt-2">
                      {STYLE_MODES.map((mode) => (
                        <button
                          key={mode.value}
                          onClick={() => setStyleMode(mode.value)}
                          className={`w-16 h-[78px] p-2 !pt-3 rounded-lg border text-xs transition-all flex flex-col gap-[10px] items-center ${
                            styleMode === mode.value
                              ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                              : 'bg-zinc-950 border-[#697D95] text-[#A1A1AA] hover:border-zinc-700'
                          }`}
                          data-testid={`style-mode-${mode.value}`}
                        >
                          <i className={`text-white ${mode.className} mt-0`} />
                          <span className='text-[10px] leading-3 text-inherit'>
                            {mode.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Colors */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-[#A1A1AA]">
                        {styleMode === 'artistic' ? 'Primary Color' : 'Shape Color'}
                      </Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={fgColor}
                          onChange={(e) => setFgColor(e.target.value)}
                          className="max-w-10 h-10 rounded cursor-pointer border-2 border-zinc-700"
                          data-testid="fg-color-input"
                          disabled={enableGradient}
                        />
                        <Input
                          value={fgColor}
                          onChange={(e) => setFgColor(e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-xs font-mono"
                          disabled={enableGradient}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-[#A1A1AA]">Background</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={bgColor}
                          onChange={(e) => setBgColor(e.target.value)}
                          className="max-w-10 h-10 rounded cursor-pointer border-2 border-zinc-700"
                          data-testid="bg-color-input"
                        />
                        <Input
                          value={bgColor}
                          onChange={(e) => setBgColor(e.target.value)}
                          className="bg-zinc-950 border-zinc-800 text-xs font-mono"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4 flex-col lg:flex-row">
                    <div className="flex items-center justify-between p-3 rounded-md bg-zinc-950 border border-zinc-800 w-full">
                      <Label className="text-xs text-[#A1A1AA]">Transparent Background</Label>
                      <button
                        onClick={() => setEnableTranparentBG(!enableTransparentBG)}
                        className={`w-10 h-6 rounded-full transition-all ${
                          enableTransparentBG ? 'bg-primary-500' : 'bg-zinc-700'
                        }`}
                        data-testid="enable-gradient-toggle"
                      >
                        <div
                          className={`w-4 h-4 rounded-full bg-white transition-transform ${
                            enableTransparentBG ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-md bg-zinc-950 border border-zinc-800 w-full">
                      <Label className="text-xs text-[#A1A1AA]">Enable Gradient</Label>
                      <button
                        onClick={() => setEnableGradient(!enableGradient)}
                        className={`w-10 h-6 rounded-full transition-all ${
                          enableGradient ? 'bg-primary-500' : 'bg-zinc-700'
                        }`}
                        data-testid="enable-gradient-toggle"
                      >
                        <div
                          className={`w-4 h-4 rounded-full bg-white transition-transform ${
                            enableGradient ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  {/* Gradient Toggle - Available for all modes */}

                  <hr className="border-[#34383b]" />


                  {/* Gradient Options - Show when enabled */}
                  {enableGradient && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-[#A1A1AA]">Gradient Start</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={gradientStart}
                              onChange={(e) => setGradientStart(e.target.value)}
                              className="max-w-10 h-10 rounded cursor-pointer border-2 border-zinc-700"
                              data-testid="gradient-start-input"
                            />
                            <Input
                              value={gradientStart}
                              onChange={(e) => setGradientStart(e.target.value)}
                              className="bg-zinc-950 border-zinc-800 text-xs font-mono"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-[#A1A1AA]">Gradient End</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="color"
                              value={gradientEnd}
                              onChange={(e) => setGradientEnd(e.target.value)}
                              className="max-w-10 h-10 rounded cursor-pointer border-2 border-zinc-700"
                              data-testid="gradient-end-input"
                            />
                            <Input
                              value={gradientEnd}
                              onChange={(e) => setGradientEnd(e.target.value)}
                              className="bg-zinc-950 border-zinc-800 text-xs font-mono"
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-[#A1A1AA]">Gradient Direction</Label>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
                          {GRADIENT_DIRECTIONS.map((dir) => (
                            <button
                              key={dir.value}
                              onClick={() => setGradientDirection(dir.value)}
                              className={`p-2 rounded-md border text-xs transition-all ${
                                gradientDirection === dir.value
                                  ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                                  : 'bg-zinc-950 border-zinc-800 text-[#A1A1AA] hover:border-zinc-700'
                              }`}
                              data-testid={`gradient-dir-${dir.value}`}
                            >
                              {dir.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {  enableGradient && gradientDirection === 'radial' && <div>
                        <Label className="text-xs text-[#A1A1AA]">Gradient Radius</Label>
                          <div className="flex gap-2 mt-2">
                            <Slider defaultValue={[gradientRadius]} onValueChange={(e) => setGradientRadius(e[0])} />
                          </div>
                      </div> }

                    </>
                  )}

                  {/* Artistic Mode Extra Colors */}
                  {styleMode === 'artistic' && !enableGradient && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-[#A1A1AA]">Accent Color</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="color"
                            value={accentColor}
                            onChange={(e) => setAccentColor(e.target.value)}
                            className="max-w-10 h-10 rounded cursor-pointer border-2 border-zinc-700"
                            data-testid="accent-color-input"
                          />
                          <Input
                            value={accentColor}
                            onChange={(e) => setAccentColor(e.target.value)}
                            className="bg-zinc-950 border-zinc-800 text-xs font-mono"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-[#A1A1AA]">Corner Color</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="color"
                            value={cornerColor}
                            onChange={(e) => setCornerColor(e.target.value)}
                            className="max-w-10 h-10 rounded cursor-pointer border-2 border-zinc-700"
                            data-testid="corner-color-input"
                          />
                          <Input
                            value={cornerColor}
                            onChange={(e) => setCornerColor(e.target.value)}
                            className="bg-zinc-950 border-zinc-800 text-xs font-mono"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Artistic Curves Toggle */}
                  {styleMode === 'artistic' && false && (
                    <div className="flex items-center justify-between p-3 rounded-md bg-zinc-950 border border-zinc-800">
                      <Label className="text-xs text-[#A1A1AA]">Enable Curves Overlay</Label>
                      <button
                        onClick={() => setEnableCurves(!enableCurves)}
                        className={`w-10 h-6 rounded-full transition-all ${
                          enableCurves ? 'bg-primary-500' : 'bg-zinc-700'
                        }`}
                        data-testid="enable-curves-toggle"
                      >
                        <div
                          className={`w-4 h-4 rounded-full bg-white transition-transform ${
                            enableCurves ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  )}

                  {/* Corner Style - Only show for basic mode */}
                  {/* {styleMode === "basic" && (
                      <div>
                        <Label className="text-xs text-[#A1A1AA]">
                          Corner Style
                        </Label>
                        <div className="grid grid-cols-4 gap-2 mt-2">
                          {CORNER_STYLES.map((style) => (
                            <button
                              key={style.value}
                              onClick={() => setCornerStyle(style.value)}
                              className={`p-2 rounded-md border text-xs transition-all ${
                                cornerStyle === style.value
                                  ? "bg-primary-500/20 border-primary-500 text-primary-400"
                                  : "bg-zinc-950 border-zinc-800 text-[#A1A1AA] hover:border-zinc-700"
                              }`}
                              data-testid={`corner-style-${style.value}`}
                            >
                              <style.icon className="w-4 h-4 mx-auto mb-1" />
                              {style.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )} */}

                  {/* Eye Frame Shape */}
                    <div>
                      <Label className="text-xs text-[#A1A1AA]">Eye Frame Shape</Label>
                      <div className="flex flex-wrap gap-[10px] mt-2">
                        {EYE_FRAME_SHAPES.map((shape) => (
                          <button
                            key={shape.value}
                            onClick={() => setEyeFrameShape(shape.value)}
                            className={`w-16 h-[78px] p-2 !pt-3 rounded-lg border text-xs transition-all flex flex-col gap-[10px] items-center ${
                              eyeFrameShape === shape.value
                                ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                                : 'bg-zinc-950 border-[#697D95] text-[#A1A1AA] hover:border-zinc-700'
                            }`}
                            data-testid={`eye-frame-${shape.value}`}
                          >
                            <i className={`text-white ${shape.className}`} />
                            <span className='text-[10px] leading-3 text-inherit'>
                              {shape.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                  <div>
                    <Label className="text-xs text-[#A1A1AA]">Border Color</Label>
                    <p className="text-xs text-zinc-500 mb-1">Custom color for finder patterns</p>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={eyeColor}
                        onChange={(e) => setEyeColor(e.target.value)}
                        className="max-w-10 h-10 rounded cursor-pointer border-2 border-zinc-700"
                        data-testid="eye-color-input"
                      />
                      <Input
                        value={eyeColor}
                        onChange={(e) => setEyeColor(e.target.value)}
                        className="bg-zinc-950 border-zinc-800 text-xs font-mono"
                      />
                    </div>
                  </div>

                  <hr className="border-[#34383b]" />

                  {/* Eye Ball Shape */}
                  <div>
                    <Label className="text-xs text-[#A1A1AA]">Eye Ball Shape</Label>
                    <p className="text-xs text-zinc-500 mb-1">Inner element of finder patterns</p>
                    <div className="flex flex-wrap gap-[10px] mt-2">
                      {EYE_BALL_SHAPES.map((shape) => (
                        <button
                          key={shape.value}
                          onClick={() => setEyeBallShape(shape.value)}
                          className={`w-16 h-[78px] p-2 !pt-3 rounded-lg border text-xs transition-all flex flex-col gap-[10px] items-center ${
                            eyeBallShape === shape.value
                              ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                              : 'bg-zinc-950 border-[#697D95] text-[#A1A1AA] hover:border-zinc-700'
                          }`}
                          data-testid={`eye-ball-${shape.value}`}
                        >
                          <i className={`text-white ${shape.className}`} />
                          <span className='text-[10px] leading-3 text-inherit'>
                            {shape.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="logo" className="space-y-4">
                  {/* Center Logo */}
                  <div>
                    <Label className="text-xs text-[#A1A1AA] mb-2 block">Upload Center Logo</Label>
                    <div
                      className="drop-zone"
                      onClick={() => document.getElementById('logo-input').click()}
                      data-testid="logo-upload-zone"
                    >
                      {logoFile ? (
                        <div className="flex items-center gap-2">
                          <ImageIcon className="w-6 h-6 text-primary-500" />
                          <span className="text-sm text-zinc-300">{logoFile?.name || 'Logo.png'}</span>
                        </div>
                      ) : (
                        <>
                          <ImageIcon className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                          <p className="text-sm text-[#A1A1AA]">Add logo to QR center</p>
                        </>
                      )}
                    </div>
                    <input
                      id="logo-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleLogoUpload(e.target.files[0])}
                    />
                    {logoFile && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setLogoFile(null);
                          setLogoBase64(null);
                        }}
                        className="w-full border-zinc-700 text-[#A1A1AA] mt-2"
                        data-testid="remove-logo-btn"
                      >
                        Remove Logo
                      </Button>
                    )}
                  </div>

                  {/* Background Image */}
                  {/* <div>
                      <Label className="text-xs text-[#A1A1AA] mb-2 block">Background Image</Label>
                      <div
                        className="drop-zone"
                        onClick={() => document.getElementById("bg-image-input").click()}
                        data-testid="bg-image-upload-zone"
                      >
                        {bgImageFile ? (
                          <div className="flex items-center gap-2">
                            <ImageIcon className="w-6 h-6 text-green-500" />
                            <span className="text-sm text-zinc-300">{bgImageFile.name}</span>
                          </div>
                        ) : (
                          <>
                            <Palette className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                            <p className="text-sm text-[#A1A1AA]">Add background image</p>
                          </>
                        )}
                      </div>
                      <input
                        id="bg-image-input"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleBgImageUpload(e.target.files[0])}
                      />
                      {bgImageFile && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setBgImageFile(null);
                            setBgImageBase64(null);
                          }}
                          className="w-full border-zinc-700 text-[#A1A1AA] mt-2"
                          data-testid="remove-bg-image-btn"
                        >
                          Remove Background
                        </Button>
                      )}
                    </div> */}
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="px-0 lg:pl-6 w-full lg:max-w-[380px] h-fit flex flex-col sticky top-[90px]">
            <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <i className="garuda-download text-primary-500 max-h-[20px]" />
              Download QR Code
            </h3>
            <div className="flex-1 flex items-center justify-center">
              {qrContent && isLoadingGenerate ? (
                <div className="bg-white p-4 rounded-lg shadow-2xl animate-fade-in">
                  <img
                    src="/assets/loading-qr.gif"
                    alt="Preview QR Code"
                    className="max-w-full"
                    style={{ width: qrSize, height: qrSize }}
                    data-testid="qr-preview-image"
                  />
                </div>
              ) : qrPreview && !isLoadingGenerate ? (
                <div className="bg-white p-4 rounded-lg shadow-2xl animate-fade-in">
                  <img
                    src={qrPreview}
                    alt="Preview QR Code"
                    className="max-w-full"
                    style={{ width: qrSize, height: qrSize }}
                    data-testid="qr-preview-image"
                  />
                </div>
              ) : (
                <div className="flex min-h-[280px] animate-fade-in">
                  <div className="text-center text-zinc-500">
                    <img
                      src="/assets/placeholder-qrcode.png"
                      alt="QR Placeholder"
                      className="mx-auto max-w-full"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="mb-4 mt-8">
              {/* Download Button */}
              <Button
                onClick={handleDownload}
                disabled={!qrContent || isLoading}
                className="w-full"
                size="xl"
                data-testid="download-btn"
              >
                {isLoading ? (
                  <div className="spinner mr-2" />
                ) : (
                  <Download className="w-5 h-5 mr-2" />
                )}
                Download
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QRForm;
